import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { Product } from '@/decodo/blocks/PricingCards/product';

import IconCoinsHand from '~/icons/coins-hand.svg';
import IconShield from '~/icons/shield-tick.svg';
import IconAlipay from '~/svg/colorfulPayments/IconAlipay.svg';
import IconAmex from '~/svg/colorfulPayments/IconAmex.svg';
import IconApplePay from '~/svg/colorfulPayments/IconApplePay.svg';
import IconBitcoin from '~/svg/colorfulPayments/IconBitcoin.svg';
import IconEtherium from '~/svg/colorfulPayments/IconEtherium.svg';
import IconGooglePay from '~/svg/colorfulPayments/IconGooglePay.svg';
import IconMastercard from '~/svg/colorfulPayments/IconMastercard.svg';
import IconPaypal from '~/svg/colorfulPayments/IconPaypalF.svg';
import IconTether from '~/svg/colorfulPayments/IconTether.svg';
import IconUnionPay from '~/svg/colorfulPayments/IconUnionPay.svg';
import IconVisa from '~/svg/colorfulPayments/IconVisa.svg';

const productsToExcludeCrypto: Product[] = [
  'datacenter_ip',
  'isp_gb',
  'isp_ip',
  'dedicated_isp',
];

type PaymentMethodsProps = {
  product: Product;
  title?: string;
  description?: string;
  showMoneyBack?: boolean;
};

export function PaymentMethods({
  product,
  title,
  description,
  showMoneyBack,
}: PaymentMethodsProps) {
  const { texts, lang } = useTranslation();

  return (
    <div className='dark:text-gray-dark-400 mt-9 flex w-full flex-col items-start justify-between gap-6 md:mt-[3.75rem] md:flex-row md:gap-6'>
      {showMoneyBack && (
        <div>
          <p className='text-body-m flex gap-2 text-left'>
            <IconCoinsHand className='h-4 w-4' />
            {texts.pricing_cards.money_back.money_back}
          </p>
        </div>
      )}
      <div
        className={cn(
          showMoneyBack
            ? 'flex flex-col gap-6 md:items-end'
            : 'flex w-full flex-col justify-between gap-6 md:flex-row'
        )}
      >
        <div className='w-fit'>
          <div className={cn(showMoneyBack ? 'md:text-right' : 'text-left')}>
            <p
              className={cn(
                'text-body-m flex flex-row items-center gap-2',
                showMoneyBack && 'md:justify-end'
              )}
            >
              <IconShield className='h-4 w-4' />

              {title || texts.pricing_cards.payment_methods.ssl_secure_payment}
            </p>
            <p className='text-body-s'>
              {description ||
                texts.pricing_cards.payment_methods.ssl_protected_information}
            </p>
          </div>
        </div>
        <div className='flex h-fit flex-wrap justify-start gap-2 md:justify-evenly'>
          {lang === 'zh-cn' && (
            <>
              <PaymentMethodWrapper>
                <IconAlipay />
              </PaymentMethodWrapper>

              <PaymentMethodWrapper>
                <IconUnionPay />
              </PaymentMethodWrapper>

              <PaymentMethodWrapper>
                <IconTether />
              </PaymentMethodWrapper>
            </>
          )}
          <PaymentMethodWrapper>
            <IconVisa />
          </PaymentMethodWrapper>
          <PaymentMethodWrapper>
            <IconMastercard />
          </PaymentMethodWrapper>

          <PaymentMethodWrapper>
            <IconPaypal />
          </PaymentMethodWrapper>

          <PaymentMethodWrapper>
            <IconAmex />
          </PaymentMethodWrapper>

          {!productsToExcludeCrypto.includes(product) && (
            <PaymentMethodWrapper>
              <IconBitcoin />
            </PaymentMethodWrapper>
          )}
          {!productsToExcludeCrypto.includes(product) && (
            <PaymentMethodWrapper>
              <IconEtherium />
            </PaymentMethodWrapper>
          )}
          <PaymentMethodWrapper>
            <IconApplePay />
          </PaymentMethodWrapper>

          <PaymentMethodWrapper>
            <IconGooglePay />
          </PaymentMethodWrapper>
        </div>
      </div>
    </div>
  );
}

function PaymentMethodWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className='rounded-[5px] border border-solid border-gray-200'>
      {children}
    </div>
  );
}
